import { Stack, Typography, useTheme } from "@mui/material";
import type { Control } from "react-hook-form";
import {
	ClientPropertyBuildingDispositionEnum,
	ClientPropertyBuildingMaterialEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type {
	BuildingFormInput,
	BuildingFormOutput,
} from "web_myplann/Portfolio/Forms/BuildingShared/buildingFormSchema";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSelect";
import { FlatRowFormSwitch } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSwitch";
import { FlatRowFormTextarea } from "web_myplann/Shared/Components/FlatRow/FlatRowFormTextarea";
import { useEnumSelectOptions } from "web_myplann/Shared/Hooks/useEnumSelectOptions";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import { createFieldGroup } from "web_myplann/Shared/Validation/fieldGroupDeps";

type BuildingFormFieldsProps = {
	control: Control<BuildingFormInput, unknown, BuildingFormOutput>;
	descriptionLabel: string;
};

const PropertyResources = Resources.PortfolioDetail.Property;
const FormResources = Resources.Portfolio.AddRealEstate;

const minimumGroup = createFieldGroup<BuildingFormInput>()([
	"location",
	"locationMunicipality",
	"description",
]);

export const BuildingFormFields = ({
	control,
	descriptionLabel,
}: BuildingFormFieldsProps) => {
	const theme = useTheme();
	const { t } = useResource();

	const dispositionOptions = useEnumSelectOptions(
		ClientPropertyBuildingDispositionEnum,
		PropertyResources.DispositionValue,
		[ClientPropertyBuildingDispositionEnum.None],
	);
	const buildingMaterialOptions = useEnumSelectOptions(
		ClientPropertyBuildingMaterialEnum,
		PropertyResources.BuildingMaterialValue,
		[
			ClientPropertyBuildingMaterialEnum.None,
			ClientPropertyBuildingMaterialEnum.Empty,
		],
	);

	return (
		<>
			<Typography variant="portfolioSectionLabel">
				{t(FormResources.Sections.location)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name="location"
					deps={minimumGroup.depsFor("location")}
					label={t(PropertyResources.StreetAndNumber)}
					placeholder={t(FormResources.Placeholders.optional)}
				/>
				<FlatRowFormInput
					control={control}
					name="locationMunicipality"
					deps={minimumGroup.depsFor("locationMunicipality")}
					label={t(PropertyResources.Municipality)}
					placeholder={t(FormResources.Placeholders.optional)}
				/>
				<FlatRowFormInput
					control={control}
					name="locationZipCode"
					label={t(PropertyResources.ZipCode)}
					placeholder={t(FormResources.Placeholders.optional)}
					isLast
				/>
			</Stack>

			<Typography variant="portfolioSectionLabel">
				{t(FormResources.Sections.parameters)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormSelect
					control={control}
					name="disposition"
					label={t(PropertyResources.Disposition)}
					options={dispositionOptions}
				/>
				<FlatRowFormSelect
					control={control}
					name="buildingMaterial"
					label={t(PropertyResources.BuildingMaterial)}
					options={buildingMaterialOptions}
				/>
				<FlatRowFormInput
					control={control}
					name="numberOfFloors"
					type="number"
					label={t(PropertyResources.NumberOfFloors)}
					placeholder={t(FormResources.Placeholders.optional)}
				/>
				<FlatRowFormInput
					control={control}
					name="usableArea"
					type="number"
					label={t(PropertyResources.UsableArea)}
					placeholder={t(FormResources.Placeholders.optional)}
					suffix="m²"
				/>
				<FlatRowFormInput
					control={control}
					name="floorArea"
					type="number"
					label={t(PropertyResources.FloorArea)}
					placeholder={t(FormResources.Placeholders.optional)}
					suffix="m²"
					isLast
				/>
			</Stack>

			<Typography variant="portfolioSectionLabel">
				{t(FormResources.Sections.amenities)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormSwitch
					control={control}
					name="terrace"
					label={t(PropertyResources.Terrace)}
				/>
				<FlatRowFormSwitch
					control={control}
					name="loggia"
					label={t(PropertyResources.Loggia)}
				/>
				<FlatRowFormSwitch
					control={control}
					name="balcony"
					label={t(PropertyResources.Balcony)}
				/>
				<FlatRowFormSwitch
					control={control}
					name="garage"
					label={t(PropertyResources.Garage)}
				/>
				<FlatRowFormSwitch
					control={control}
					name="cellar"
					label={t(PropertyResources.Cellar)}
				/>
				<FlatRowFormSwitch
					control={control}
					name="elevator"
					label={t(PropertyResources.Elevator)}
				/>
				<FlatRowFormSwitch
					control={control}
					name="barrierFree"
					label={t(PropertyResources.BarrierFree)}
				/>
				<FlatRowFormSwitch
					control={control}
					name="swimmingPool"
					label={t(PropertyResources.SwimmingPool)}
					isLast
				/>
			</Stack>

			<Typography variant="portfolioSectionLabel">
				{t(FormResources.Sections.valueAndDates)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name="interiorValue"
					type="number"
					label={t(PropertyResources.InteriorPrice)}
					placeholder={t(FormResources.Placeholders.optional)}
					suffix="Kč"
				/>
				<FlatRowFormDatePicker
					control={control}
					name="purchaseDate"
					label={t(PropertyResources.PurchaseDate)}
					views={["year", "month", "day"]}
					displayFormat={DateFormat.Display}
					isLast
				/>
			</Stack>

			<Typography variant="portfolioSectionLabel">
				{t(FormResources.Sections.note)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormTextarea
					control={control}
					name="description"
					deps={minimumGroup.depsFor("description")}
					label={descriptionLabel}
					placeholder={t(FormResources.Placeholders.optional)}
					rows={1}
					isLast
				/>
			</Stack>

			<Typography variant="portfolioSectionLabel">
				{t(Resources.Portfolio.AddArt.Sections.investment)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormSwitch
					control={control}
					name="forInvestments"
					label={t(Resources.Portfolio.AddArt.Fields.forInvestments)}
					isLast
				/>
			</Stack>
		</>
	);
};
